import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';
import { twx } from '@/utils/tailwind';

export interface InlineEmptyStateProps {
  icon: IconDefinition;
  title: string;
  /** Optional follow-up line — context, hint, or short instruction. */
  message?: ReactNode;
  /** "md" (default) fits a column or pane. "sm" is for narrow sidebars
   * (e.g., the Scout scouted list). */
  size?: 'sm' | 'md';
  className?: string;
}

type FAIconStyle = React.CSSProperties & { [key: `--fa-${string}`]: string };

export const InlineEmptyState = ({
  icon,
  title,
  message,
  size = 'md',
  className,
}: InlineEmptyStateProps) => {
  const isSmall = size === 'sm';
  return (
    <div
      className={twx(
        'flex flex-col items-center text-center gap-2',
        isSmall ? 'py-6 px-3' : 'py-10 px-4',
        className,
      )}
    >
      <FontAwesomeIcon
        icon={icon}
        style={
          {
            width: isSmall ? 32 : 44,
            height: isSmall ? 32 : 44,
            '--fa-primary-color': 'var(--color-secondary-emphasized)',
            '--fa-secondary-color': 'var(--color-secondary-main)',
            '--fa-secondary-opacity': '0.65',
          } as FAIconStyle
        }
      />
      <p
        className={twx(
          'font-medium text-content-text',
          isSmall ? 'text-sm' : 'text-base font-semibold',
        )}
      >
        {title}
      </p>
      {message && (
        <p
          className={twx(
            'text-content-subtle leading-relaxed max-w-xs',
            isSmall ? 'text-xs' : 'text-sm',
          )}
        >
          {message}
        </p>
      )}
    </div>
  );
};
